import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  type HTMLMotionProps,
} from "framer-motion";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

type CursorFollowingGlassButtonProps = Omit<HTMLMotionProps<"button">, "children"> & {
  children?: ReactNode;
  containerRef: RefObject<HTMLElement | null>;
  visible: boolean;
};

const FOLLOW_SPRING = { damping: 25, stiffness: 150, mass: 0.5 };
const EDGE_PADDING = 18;
const MATERIALIZATION_DUST = Array.from({ length: 18 }, (_, index) => {
  const angle = (index / 18) * Math.PI * 2;
  const spread = 33 + (index % 4) * 4;

  return {
    left: `${50 + Math.cos(angle) * spread}%`,
    top: `${50 + Math.sin(angle) * 31}%`,
    width: `${1.1 + (index % 3) * 0.45}px`,
    height: `${1.1 + (index % 3) * 0.45}px`,
    "--dust-delay": `${index * 13}ms`,
    "--dust-x": `${Math.cos(angle) * (18 + (index % 4) * 4)}px`,
    "--dust-y": `${Math.sin(angle) * (14 + (index % 3) * 4)}px`,
  } as CSSProperties;
});

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

export function CursorFollowingGlassButton({
  children,
  className = "",
  containerRef,
  disabled,
  tabIndex,
  type = "button",
  visible,
  ...props
}: CursorFollowingGlassButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const smoothX = useSpring(x, FOLLOW_SPRING);
  const smoothY = useSpring(y, FOLLOW_SPRING);
  const prefersReducedMotion = useReducedMotion();
  const latestPointerRef = useRef<{ x: number; y: number } | null>(null);
  const hasInitialPositionRef = useRef(false);
  const [canFollow, setCanFollow] = useState(false);
  const [positioned, setPositioned] = useState(false);

  useEffect(() => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateCapability = () => {
      setCanFollow(finePointer.matches && !prefersReducedMotion);
    };

    updateCapability();
    finePointer.addEventListener("change", updateCapability);
    return () => finePointer.removeEventListener("change", updateCapability);
  }, [prefersReducedMotion]);

  useEffect(() => {
    if (!canFollow) {
      latestPointerRef.current = null;
      return;
    }

    const rememberPointer = (event: PointerEvent) => {
      latestPointerRef.current = { x: event.clientX, y: event.clientY };
    };

    window.addEventListener("pointermove", rememberPointer, { passive: true });
    return () => window.removeEventListener("pointermove", rememberPointer);
  }, [canFollow]);

  useEffect(() => {
    const container = containerRef.current;
    const button = buttonRef.current;

    if (!visible || !container || !button) {
      hasInitialPositionRef.current = false;
      setPositioned(false);
      return;
    }

    let frame = 0;

    const placeButton = (centerX: number, centerY: number) => {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const nextX = clamp(
        centerX - buttonRect.width / 2,
        EDGE_PADDING,
        containerRect.width - buttonRect.width - EDGE_PADDING,
      );
      const nextY = clamp(
        centerY - buttonRect.height / 2,
        EDGE_PADDING,
        containerRect.height - buttonRect.height - EDGE_PADDING,
      );

      if (!hasInitialPositionRef.current) {
        x.set(nextX);
        y.set(nextY);
        smoothX.jump(nextX);
        smoothY.jump(nextY);
        hasInitialPositionRef.current = true;
      } else {
        x.set(nextX);
        y.set(nextY);
      }

      const buttonCenterX = nextX + buttonRect.width / 2;
      const buttonCenterY = nextY + buttonRect.height / 2;
      const lightAngle = Math.atan2(
        -buttonCenterY,
        containerRect.width / 2 - buttonCenterX,
      ) * (180 / Math.PI) + 90;
      button.style.setProperty("--glass-light-angle", `${lightAngle}deg`);
      setPositioned(true);
    };

    const placeAtPointerOrRest = () => {
      const containerRect = container.getBoundingClientRect();
      const latestPointer = latestPointerRef.current;

      if (
        canFollow
        && latestPointer
        && latestPointer.x >= containerRect.left
        && latestPointer.x <= containerRect.right
        && latestPointer.y >= containerRect.top
        && latestPointer.y <= containerRect.bottom
      ) {
        placeButton(
          latestPointer.x - containerRect.left,
          latestPointer.y - containerRect.top,
        );
        return;
      }

      const buttonRect = button.getBoundingClientRect();
      const rightInset = clamp(containerRect.width * 0.09, 32, 132);
      const bottomInset = clamp(containerRect.height * 0.18, 92, 176);
      placeButton(
        containerRect.width - buttonRect.width / 2 - rightInset,
        containerRect.height - buttonRect.height / 2 - bottomInset,
      );
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!canFollow) return;
      const containerRect = container.getBoundingClientRect();
      placeButton(event.clientX - containerRect.left, event.clientY - containerRect.top);
    };

    frame = window.requestAnimationFrame(placeAtPointerOrRest);
    container.addEventListener("pointermove", handlePointerMove, { passive: true });

    const resizeObserver = new ResizeObserver(placeAtPointerOrRest);
    resizeObserver.observe(container);

    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      container.removeEventListener("pointermove", handlePointerMove);
    };
  }, [canFollow, containerRef, smoothX, smoothY, visible, x, y]);

  const isVisible = visible && positioned;
  const horizontalPosition = prefersReducedMotion ? x : smoothX;
  const verticalPosition = prefersReducedMotion ? y : smoothY;

  return (
    <motion.div
      className="cursor-following-glass-button"
      data-following={canFollow ? "true" : "false"}
      data-visible={isVisible ? "true" : "false"}
      style={{ x: horizontalPosition, y: verticalPosition }}
    >
      <span aria-hidden="true" className="cursor-following-glass-button__dust">
        {MATERIALIZATION_DUST.map((style, index) => (
          <span key={index} style={style} />
        ))}
      </span>
      <motion.button
        {...props}
        animate={{
          clipPath: isVisible
            ? "inset(0% 0% round 999px)"
            : "inset(44% 48% round 999px)",
          filter: isVisible ? "blur(0px)" : "blur(11px)",
          opacity: isVisible ? 1 : 0,
          scale: isVisible ? 1 : 0.82,
        }}
        aria-hidden={!isVisible}
        className={`cursor-following-glass-button__control ${className}`}
        disabled={disabled}
        initial={false}
        ref={buttonRef}
        tabIndex={isVisible ? tabIndex : -1}
        transition={prefersReducedMotion
          ? { duration: 0 }
          : { duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        type={type}
        whileTap={prefersReducedMotion || disabled ? undefined : { scale: 0.97 }}
      >
        <span aria-hidden="true" className="cursor-following-glass-button__material" />
        <span aria-hidden="true" className="cursor-following-glass-button__rim" />
        <span className="cursor-following-glass-button__label">{children}</span>
      </motion.button>
    </motion.div>
  );
}
